import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';

import { BaseViewerComponent } from '../base-viewer/base-viewer.component';

import { AsyncPipe } from '@angular/common';
import { SharedLanguageService } from '@language';
import { TranslateModule } from '@ngx-translate/core';
import PostalMime, { Email } from 'postal-mime';
import { EMPTY, Observable, Subject } from 'rxjs';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { GenericActionFailureToast, PartialToast } from '../../../toasts/toast.module';
import { ViewerSandboxService } from '../../sandbox/viewer-sandbox.service';

@Component({
    selector: 'sh-eml-viewer',
    templateUrl: './eml-viewer.component.html',
    styleUrls: ['./eml-viewer.compoment.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [AsyncPipe, SafeHtmlPipe, TranslateModule]
})
export class EmlViewerComponent extends BaseViewerComponent implements OnChanges, OnDestroy {
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    readonly mail$: Subject<Email> = new Subject<Email>();
    private readonly parser: PostalMime;

    @Input() toastId!: number;

    constructor(private readonly sandbox: ViewerSandboxService) {
        super();
        this.parser = new PostalMime();
    }

    ngOnChanges(_: SimpleChanges): void {
        if (this.blobUrl) {
            this.fetchBlobFromUrl(this.blobUrl).then(blob => this.displayMail(blob));
        }
    }

    ngOnDestroy(): void {
        this.mail$.complete();
    }

    private displayMail(rawMail: Blob) {
        this.parser
            .parse(rawMail)
            .then(mail => {
                if (mail.html) {
                    mail.attachments
                        .filter(attachment => attachment.disposition == 'inline')
                        .filter(attachment => !!attachment.contentId)
                        .forEach(attachment => {
                            const contentId = attachment.contentId!.replace('<', '').replace('>', '');
                            const base64string = this.arrayBufferToBase64(attachment.content);
                            mail.html = mail.html!.replace(
                                `cid:${contentId}`,
                                `data:${attachment.mimeType};base64,${base64string}`
                            );
                        });
                }
                this.mail$.next(mail);
            })
            .catch(error => {
                this.handleExportError(this.toastId, error);
            });
    }

    private arrayBufferToBase64(buffer: any): string {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    private async fetchBlobFromUrl(blobUrl: string): Promise<Blob> {
        return fetch(blobUrl).then(r => r.blob());
    }

    private handleExportError(toastId: number, e: any): Observable<never> {
        console.error(e);
        this.sandbox.updateToast(toastId, this.createViewErrorToast());
        return EMPTY;
    }

    private createViewErrorToast(): PartialToast {
        return GenericActionFailureToast(this.translateService.instant('viewer.toasts.error'));
    }
}
